import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_LINKS = [
  { label: "首页", to: "/" },
  { label: "影视资讯", to: "/news" },
  { label: "作品", to: "/works" },
  { label: "演员", to: "/actors" },
  { label: "关于", to: "/about" },
  { label: "联系我们", to: "/contact" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const isHome = location.pathname === "/";
  const solid = !isHome || scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileSearchOpen(false);
  }, [location.pathname]);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    navigate(`/news?q=${encodeURIComponent(trimmed)}`);
    setQuery("");
    setMobileOpen(false);
  };

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  const textColor = solid ? "text-foreground-900" : "text-background-50";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-gradient-to-b from-foreground-950/60 to-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link to="/" className={`flex items-center gap-2 shrink-0 ${textColor}`}>
          <span className="w-9 h-9 rounded-md bg-primary-500 flex items-center justify-center text-background-50 text-lg font-bold">
            筱
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg md:text-xl font-bold tracking-wide">筱田优</span>
            <span className="text-[10px] tracking-[0.3em] opacity-70 font-label">FILMS</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium transition-colors relative py-1 ${
                isActive(link.to)
                  ? "text-accent-500"
                  : solid
                    ? "text-foreground-700 hover:text-primary-600"
                    : "text-background-50/85 hover:text-background-50"
              }`}
            >
              {link.label}
              {isActive(link.to) && (
                <span className="absolute -bottom-0.5 left-0 right-0 h-0.5 rounded-full bg-accent-500" />
              )}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form
            onSubmit={submitSearch}
            className={`hidden md:flex items-center gap-2 rounded-full px-3 h-9 border transition-colors ${
              solid
                ? "bg-background-100 border-background-200"
                : "bg-background-50/15 border-background-50/30"
            }`}
          >
            <i className={`ri-search-line text-sm ${solid ? "text-foreground-400" : "text-background-50/80"}`} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索资讯 / 作品 / 演员"
              className={`bg-transparent outline-none text-sm w-40 xl:w-52 ${
                solid
                  ? "text-foreground-900 placeholder:text-foreground-400"
                  : "text-background-50 placeholder:text-background-50/70"
              }`}
            />
          </form>

          <button
            type="button"
            aria-label="搜索"
            onClick={() => setMobileSearchOpen((v) => !v)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-full cursor-pointer ${
              solid ? "text-foreground-800 bg-background-100" : "text-background-50 bg-background-50/15"
            }`}
          >
            <i className="ri-search-line text-base" />
          </button>

          <button
            type="button"
            aria-label="菜单"
            onClick={() => setMobileOpen((v) => !v)}
            className={`lg:hidden w-9 h-9 flex items-center justify-center rounded-md cursor-pointer ${
              solid ? "text-foreground-800 bg-background-100" : "text-background-50 bg-background-50/15"
            }`}
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {mobileSearchOpen && (
        <div className="md:hidden px-4 pb-3">
          <form
            onSubmit={submitSearch}
            className="flex items-center gap-2 rounded-full px-4 h-10 bg-background-100 border border-background-200"
          >
            <i className="ri-search-line text-sm text-foreground-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索资讯 / 作品 / 演员"
              className="bg-transparent outline-none text-sm flex-1 text-foreground-900 placeholder:text-foreground-400"
            />
          </form>
        </div>
      )}

      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 px-4 py-3">
          <nav className="grid grid-cols-2 gap-2">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                  isActive(link.to)
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-800 hover:bg-background-200"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}